import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg, { getBreadcrumbSchema, getWebPageSchema } from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/home';
import { allComicsByCategory, type CategoryComic } from '@/mocks/category';
import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';

function CategoryCard({ comic }: { comic: CategoryComic }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to="/category/all"
      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 cursor-pointer transition-all duration-300 hover:-translate-y-1"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative" style={{ aspectRatio: '3/4' }}>
        <img
          src={comic.coverImage}
          alt={comic.title}
          title={`${comic.title} - ${comic.category}`}
          className="w-full h-full object-cover object-top"
        />
        {hovered && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity">
            <span className="px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium">立即阅读</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1.5">{comic.author} · {comic.latestChapter}</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="text-xs text-foreground-400">{comic.category}</span>
          <span className="text-xs text-foreground-300">·</span>
          <span className="text-xs flex items-center gap-0.5 text-foreground-400">
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-star-fill text-yellow-400 text-xs"></i></span>
            {comic.rating}
          </span>
          <span className="text-xs text-foreground-300">·</span>
          <span className="text-xs text-foreground-400">{comic.views}</span>
        </div>
      </div>
    </Link>
  );
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || categories[0];
  const comics = allComicsByCategory[slug || 'romance'] || allComicsByCategory['romance'];
  const totalCount = comics.length;

  return (
    <>
      <SchemaOrg type="WebPage" data={getWebPageSchema(
        `${category.name} - 韩漫漫画网`,
        `探索${category.name}分类下的所有精彩韩漫作品，共收录${totalCount}部漫画。每日更新，智能推荐，发现更多心仪韩漫。`,
        `http://linxituan.com/category/${category.slug}`
      )} />
      <SchemaOrg type="BreadcrumbList" data={getBreadcrumbSchema([
        { name: '首页', url: 'http://linxituan.com/' },
        { name: category.name, url: `http://linxituan.com/category/${category.slug}` },
      ])} />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 cursor-pointer transition-colors">首页</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
            <span className="text-foreground-300">分类</span>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
            <span className="text-foreground-800 font-medium">{category.name}</span>
          </nav>

          <div className="bg-secondary-50/50 rounded-2xl p-8 md:p-12 mb-12">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/3">
                <p className="text-xs font-medium text-foreground-500 mb-2">分类浏览</p>
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">{category.name}</h1>
              </div>
              <div className="md:w-2/3">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  探索{category.name}分类下的所有精彩韩漫作品。本分类共收录 {totalCount} 部漫画，涵盖多样化的故事题材与精美画风。无论你喜欢甜蜜浪漫还是扣人心弦的剧情，这里都有适合你的作品。每日更新，智能推荐，帮助你发现更多心仪漫画。
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
                  <div>
                    <p className="text-3xl md:text-4xl font-bold text-foreground-900">{totalCount}+</p>
                    <p className="text-xs text-foreground-500 mt-1">作品总量</p>
                  </div>
                  <div>
                    <p className="text-3xl md:text-4xl font-bold text-foreground-900">{Math.floor(Math.random() * 50 + 100)}+</p>
                    <p className="text-xs text-foreground-500 mt-1">本周更新</p>
                  </div>
                  <div>
                    <p className="text-3xl md:text-4xl font-bold text-foreground-900">{((comics.reduce((s, c) => s + c.rating, 0) / comics.length)).toFixed(1)}</p>
                    <p className="text-xs text-foreground-500 mt-1">平均评分</p>
                  </div>
                  <div>
                    <p className="text-3xl md:text-4xl font-bold text-foreground-900">{comics.filter(c => c.status === 'ongoing').length}</p>
                    <p className="text-xs text-foreground-500 mt-1">连载数量</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-10">{category.name}作品列表</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6 mb-16">
            {comics.map((comic) => (
              <CategoryCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}